import {
  Badge,
  Box,
  Button,
  ChakraAsyncSelect,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Progress,
  SimpleGrid,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Text,
  Tooltip,
  useColorModeValue,
  useCustomToast,
  VStack,
} from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useGetMappingByIdApi, useUpdateMappingBrandApi } from 'apis/crm-brand-trade-license-mappings';
import React, { useCallback, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FiCheck, FiInfo, FiStar, FiTag, FiTarget, FiTrendingUp } from 'react-icons/fi';

interface EditMappingModalProps {
  isOpen: boolean;
  onClose: () => void;
  mappingId: number;
}

interface FormData {
  brand: { value: number; label: string } | null;
  brandConfidence: number;
}

export const EditMappingModal: React.FC<EditMappingModalProps> = ({ isOpen, onClose, mappingId }) => {
  const toast = useCustomToast();

  // Color mode values
  const cardBgColor = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Fetch mapping details
  const { data: mappingDetails, isLoading: isLoadingDetails } = useGetMappingByIdApi(mappingId, {
    enabled: isOpen && !!mappingId,
  });

  // Update mutation
  const { mutate: updateMapping, isLoading: isUpdating } = useUpdateMappingBrandApi();

  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { isDirty },
  } = useForm<FormData>({
    defaultValues: {
      brand: null,
      brandConfidence: 0.85, // Default confidence
    },
  });

  const selectedBrand = watch('brand');

  // Get confidence color based on value
  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return 'green';
    if (confidence >= 0.6) return 'yellow';
    if (confidence >= 0.4) return 'orange';
    return 'red';
  };

  // Get confidence label
  const getConfidenceLabel = (confidence: number) => {
    if (confidence >= 0.9) return 'Excellent';
    if (confidence >= 0.8) return 'Very Good';
    if (confidence >= 0.7) return 'Good';
    if (confidence >= 0.6) return 'Fair';
    if (confidence >= 0.4) return 'Poor';
    return 'Very Poor';
  };

  // Async load brands with search functionality
  const loadBrandOptions = useCallback(async (inputValue: string) => {
    try {
      const { data } = await http.get('ums/v1/crm/management/brands', {
        params: {
          page: 1,
          limit: 50,
          name: inputValue || undefined,
        },
      });
      return (data?.data || []).map((brand: any) => ({
        value: brand.id,
        label: brand.name,
      }));
    } catch (error) {
      console.error('Error loading brands:', error);
      return [];
    }
  }, []);

  // Reset form when modal opens or mapping details are loaded
  useEffect(() => {
    if (isOpen && mappingDetails) {
      const brandOption = mappingDetails.brand
        ? {
            value: mappingDetails.brand.id,
            label: mappingDetails.brand.name,
          }
        : null;

      reset({
        brand: brandOption,
        brandConfidence: mappingDetails.brandConfidence || 0.85,
      });
    }
  }, [isOpen, mappingDetails, reset]);

  // Memoized close handler
  const onCloseModal = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  // Memoized submit handler
  const onSubmit = useCallback(
    async (data: FormData) => {
      if (!data.brand?.value) {
        toast({
          title: 'Brand Required',
          description: 'Please select a brand before saving the mapping.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      const payload = {
        brandId: data.brand.value,
        brandConfidence: data.brandConfidence,
      };

      try {
        await new Promise<void>((resolve, reject) => {
          updateMapping(
            { id: mappingId, data: payload },
            {
              onSuccess: () => {
                // Close modal after successful update
                setTimeout(() => onCloseModal(), 100);
                resolve();
              },
              onError: (error) => reject(error),
            }
          );
        });
      } catch (error) {
        // Error handling is already done in the API
        console.error('Failed to update brand mapping:', error);
      }
    },
    [updateMapping, mappingId, toast, onCloseModal]
  );

  return (
    <Modal isOpen={isOpen} onClose={onCloseModal} size='2xl' closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={3}>
            <Icon as={FiTag} color='blue.500' />
            <Text>Edit Brand Mapping</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pb={6}>
          {isLoadingDetails ? (
            <Flex justify='center' align='center' h='200px'>
              <VStack spacing={3}>
                <Progress size='xs' colorScheme='blue' isIndeterminate w='200px' />
                <Text color='gray.500'>Loading mapping details...</Text>
              </VStack>
            </Flex>
          ) : (
            <VStack spacing={6} align='stretch'>
              {/* Current Mapping Info */}
              {mappingDetails && (
                <Box p={4} bg={cardBgColor} borderRadius='lg' border='1px solid' borderColor={borderColor}>
                  <VStack spacing={3} align='stretch'>
                    <HStack spacing={2}>
                      <Icon as={FiInfo} color='blue.500' />
                      <Text fontSize='sm' fontWeight='semibold' color='blue.600'>
                        Current Mapping Information
                      </Text>
                    </HStack>
                    <SimpleGrid columns={2} spacing={4}>
                      <Box>
                        <Text fontSize='xs' color='gray.500' fontWeight='medium'>
                          Brand Name
                        </Text>
                        <Text fontSize='sm' fontWeight='semibold'>
                          {mappingDetails.entityName}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize='xs' color='gray.500' fontWeight='medium'>
                          Legal Name
                        </Text>
                        <Text fontSize='sm' fontWeight='semibold'>
                          {mappingDetails.tradeLicenseName}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize='xs' color='gray.500' fontWeight='medium'>
                          License Number
                        </Text>
                        <Text fontSize='sm' fontWeight='semibold' color='purple.600'>
                          {mappingDetails.licenseNumber}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize='xs' color='gray.500' fontWeight='medium'>
                          Current Brand
                        </Text>
                        <Text
                          fontSize='sm'
                          fontWeight='semibold'
                          color={mappingDetails.brand ? 'green.600' : 'gray.400'}>
                          {mappingDetails.brand ? mappingDetails.brand.name : 'Not mapped'}
                        </Text>
                      </Box>
                    </SimpleGrid>
                  </VStack>
                </Box>
              )}

              <Divider />

              {/* Brand Selection Section */}
              <VStack spacing={4} align='stretch'>
                <HStack spacing={2}>
                  <Icon as={FiTarget} color='green.500' />
                  <Text fontSize='lg' fontWeight='bold' color='green.600'>
                    Brand Selection
                  </Text>
                </HStack>

                <Controller
                  control={control}
                  name='brand'
                  rules={{ required: 'Brand selection is required' }}
                  render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => (
                    <FormControl isInvalid={!!error} isRequired>
                      <FormLabel>Select Brand</FormLabel>
                      <ChakraAsyncSelect
                        ref={ref}
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        loadOptions={loadBrandOptions}
                        placeholder='🔍 Search and select a brand...'
                        isClearable
                        noOptionsMessage={({ inputValue }) =>
                          inputValue ? 'No brands found' : 'Type to search brands'
                        }
                        loadingMessage={() => 'Loading brands...'}
                        styles={{
                          control: (provided: any) => ({
                            ...provided,
                            borderRadius: '8px',
                            borderColor: error ? '#E53E3E' : '#E2E8F0',
                            boxShadow: error ? '0 0 0 1px #E53E3E' : '0 0 0 1px #E2E8F0',
                            '&:hover': {
                              borderColor: error ? '#E53E3E' : '#CBD5E0',
                            },
                          }),
                        }}
                      />
                      <FormErrorMessage>{error?.message}</FormErrorMessage>
                    </FormControl>
                  )}
                />

                {/* Selected Brand Preview */}
                {selectedBrand && (
                  <Box p={3} bg='green.50' borderRadius='md' border='1px solid' borderColor='green.200'>
                    <HStack spacing={3}>
                      <Icon as={FiCheck} color='green.500' />
                      <VStack align='start' spacing={1}>
                        <Text fontSize='sm' fontWeight='semibold' color='green.800'>
                          Selected Brand
                        </Text>
                        <Text fontSize='md' fontWeight='bold' color='green.600'>
                          {selectedBrand.label}
                        </Text>
                      </VStack>
                      <Badge colorScheme='green' ml='auto'>
                        ID: {selectedBrand.value}
                      </Badge>
                    </HStack>
                  </Box>
                )}
              </VStack>

              <Divider />

              {/* Confidence Level Section */}
              <VStack spacing={4} align='stretch'>
                <HStack spacing={2}>
                  <Icon as={FiStar} color='orange.500' />
                  <Text fontSize='lg' fontWeight='bold' color='orange.600'>
                    Confidence Level
                  </Text>
                </HStack>

                <Controller
                  control={control}
                  name='brandConfidence'
                  rules={{
                    required: 'Confidence level is required',
                    min: { value: 0, message: 'Confidence must be at least 0%' },
                    max: { value: 1, message: 'Confidence must be at most 100%' },
                  }}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <FormControl isInvalid={!!error}>
                      <FormLabel>
                        Brand Mapping Confidence
                        <Tooltip label='How confident are you that this brand mapping is correct?'>
                          <Icon as={FiInfo} ml={2} color='gray.400' />
                        </Tooltip>
                      </FormLabel>

                      {/* Confidence Stats */}
                      <Box mb={4}>
                        <Stat>
                          <StatLabel fontSize='sm'>Current Confidence Score</StatLabel>
                          <StatNumber color={`${getConfidenceColor(value)}.500`}>{Math.round(value * 100)}%</StatNumber>
                          <StatHelpText>
                            <HStack spacing={1}>
                              <Icon as={FiTrendingUp} />
                              <Text>{getConfidenceLabel(value)} match</Text>
                            </HStack>
                          </StatHelpText>
                        </Stat>
                      </Box>

                      {/* Visual Progress */}
                      <Box mb={4}>
                        <Progress
                          value={value * 100}
                          colorScheme={getConfidenceColor(value)}
                          size='lg'
                          borderRadius='full'
                        />
                      </Box>

                      {/* Slider Control */}
                      <Box mb={4}>
                        <Slider
                          value={value}
                          onChange={onChange}
                          min={0}
                          max={1}
                          step={0.01}
                          colorScheme={getConfidenceColor(value)}>
                          <SliderTrack>
                            <SliderFilledTrack />
                          </SliderTrack>
                          <SliderThumb boxSize={6}>
                            <Box color={`${getConfidenceColor(value)}.500`} as={FiStar} />
                          </SliderThumb>
                        </Slider>
                      </Box>

                      {/* Precise Number Input */}
                      <HStack spacing={4}>
                        <Box flex='1'>
                          <Text fontSize='sm' mb={2} fontWeight='medium'>
                            Precise Value
                          </Text>
                          <NumberInput
                            value={value}
                            onChange={(_, valueAsNumber) => onChange(isNaN(valueAsNumber) ? 0 : valueAsNumber)}
                            min={0}
                            max={1}
                            step={0.01}
                            precision={2}>
                            <NumberInputField />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                        </Box>
                        <Box flex='1'>
                          <Text fontSize='sm' mb={2} fontWeight='medium'>
                            Percentage
                          </Text>
                          <NumberInput
                            value={Math.round(value * 100)}
                            onChange={(_, valueAsNumber) => onChange((isNaN(valueAsNumber) ? 0 : valueAsNumber) / 100)}
                            min={0}
                            max={100}
                            step={1}>
                            <NumberInputField />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                        </Box>
                      </HStack>

                      <FormErrorMessage>{error?.message}</FormErrorMessage>
                    </FormControl>
                  )}
                />

                {/* Confidence Guidelines */}
                <Box p={3} bg='blue.50' borderRadius='md' border='1px solid' borderColor='blue.200'>
                  <Text fontSize='sm' fontWeight='semibold' color='blue.800' mb={2}>
                    Confidence Guidelines:
                  </Text>
                  <VStack spacing={1} align='start'>
                    <HStack>
                      <Badge colorScheme='green' size='sm'>
                        90-100%
                      </Badge>
                      <Text fontSize='xs'>Exact match, verified manually</Text>
                    </HStack>
                    <HStack>
                      <Badge colorScheme='green' size='sm'>
                        80-89%
                      </Badge>
                      <Text fontSize='xs'>Very likely match, strong indicators</Text>
                    </HStack>
                    <HStack>
                      <Badge colorScheme='yellow' size='sm'>
                        70-79%
                      </Badge>
                      <Text fontSize='xs'>Good match, some validation needed</Text>
                    </HStack>
                    <HStack>
                      <Badge colorScheme='orange' size='sm'>
                        60-69%
                      </Badge>
                      <Text fontSize='xs'>Fair match, requires review</Text>
                    </HStack>
                    <HStack>
                      <Badge colorScheme='red' size='sm'>
                        Below 60%
                      </Badge>
                      <Text fontSize='xs'>Uncertain match, needs verification</Text>
                    </HStack>
                  </VStack>
                </Box>
              </VStack>
            </VStack>
          )}
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3} w='full' justify='space-between'>
            <HStack spacing={2}>
              {isDirty && (
                <Badge colorScheme='orange' variant='subtle'>
                  Unsaved Changes
                </Badge>
              )}
            </HStack>
            <HStack spacing={3}>
              <Button variant='outline' onClick={onCloseModal} isDisabled={isUpdating}>
                Cancel
              </Button>
              <Button
                colorScheme='blue'
                onClick={handleSubmit(onSubmit)}
                isLoading={isUpdating}
                loadingText='Updating...'
                isDisabled={isLoadingDetails || !selectedBrand}
                leftIcon={<FiCheck />}>
                Update Brand Mapping
              </Button>
            </HStack>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
